import { t } from 'i18next'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  algorithmRunStatuses,
  conformityCheckFunctionTypes,
  NO_DATA_PLACEHOLDER,
  RiskReliefSmartFunctionType,
  riskReliefSmartFunctionTypes,
  workspaceDocumentSetStatuses,
  workspaceTypes,
} from '../constants/constants'
import { ExtendedAlgorithmRun } from '../pages/WorkspaceDetailsPage/panels/WorkspaceSmartFunctionPanel'
import {
  AlgorithmRun,
  AlgorithmRunConfig,
  AlgorithmRunConformityCheckConfig,
  AlgorithmRunRiskReliefConfig,
  RiskReliefAlgorithmRun,
} from '../store/reducers/workspaceReducer'
import { AlgorithmRunStatus } from '../types/generalTypes'
import useWorkspaceDetails from './useWorkspaceDetails'

const useWorkspaceSmartFunctionData = () => {
  const { workspaceId, deviationId, documentId } = useParams()
  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const algorithmRunBase: ExtendedAlgorithmRun = useMemo(
    () => ({
      id: '0',
      key: '0',
      workspace: selectedWorkspace?.title || '',
      dataSource:
        typeof selectedWorkspace?.dataSource === 'string'
          ? selectedWorkspace?.dataSource
          : selectedWorkspace?.dataSource?.id,
      runStatus: algorithmRunStatuses.READY,
      calculatedStatus: algorithmRunStatuses.READY,
      smartFunctionName: selectedWorkspace?.algorithmName || '',
    }),
    [selectedWorkspace]
  )

  const isStatusOngoing = useCallback(
    (runs: AlgorithmRun[]) =>
      !!runs?.find(
        (run) =>
          run.runStatus === algorithmRunStatuses.ONGOING_INGESTION ||
          run.runStatus === algorithmRunStatuses.ONGOING_AI
      ),
    []
  )

  interface CalculateStatusProps {
    status?: AlgorithmRunStatus
    runs: AlgorithmRun[]
  }

  const calculateStatus = useCallback(
    ({ status, runs }: CalculateStatusProps) => {
      const latestRun = runs[runs.length - 1]
      if (isStatusOngoing(runs) && status !== algorithmRunStatuses.COMPLETE) {
        return algorithmRunStatuses.ONGOING_AI
      }
      if (latestRun?.runStatus === algorithmRunStatuses.FAILED) {
        return algorithmRunStatuses.FAILED
      }
      if (
        status === algorithmRunStatuses.PENDING &&
        latestRun.runStatus === algorithmRunStatuses.CANCELLED
      ) {
        return algorithmRunStatuses.CANCELLED
      }
      if (!status) {
        return algorithmRunStatuses.READY
      }

      return status
    },
    [isStatusOngoing]
  )

  // checking if the workspace is A RISK REL workspace with deviation that is closed. If So we will disable the smart function action dropdown
  const isRiskReliefWorkspaceDeviationClosed = useMemo(() => {
    return (
      (selectedWorkspace?.typeCode === workspaceTypes.RISK_REL &&
        selectedWorkspace?.config?.riskReliefConfig?.queryDeviations.some(
          (deviation) =>
            deviation.searchQueryId === deviationId &&
            deviation.deviationStatus === workspaceDocumentSetStatuses.CLOSED
        )) ||
      false
    )
  }, [selectedWorkspace, deviationId])

  const riskReliefSmartFunctions: ExtendedAlgorithmRun[][] = useMemo(() => {
    const runsForDeviation =
      selectedWorkspace?.algorithmRuns?.filter((run) => {
        const config = run?.config as AlgorithmRunRiskReliefConfig
        return !!config?.deviations && config.deviationId === deviationId
      }) || []
    const latestRun = runsForDeviation[runsForDeviation.length - 1]
    const algorithmRunBaseExtended: ExtendedAlgorithmRun = {
      ...algorithmRunBase,
      actionDate: latestRun?.actionDate,
      algorithmRunIndex: runsForDeviation ? runsForDeviation.length : 0,
      id: latestRun?.id,
      calculatedRecordId: latestRun?.id ?? '0',
    }

    const riskReliefSmartFunctions: ExtendedAlgorithmRun[][] = [
      [
        {
          ...algorithmRunBaseExtended,
          key: '1',
          smartFunctionName: t(
            'workspaceDetailsPage.riskReliefSmartFunction.deviationClassification'
          ),
          smartFunctionType:
            riskReliefSmartFunctionTypes.DEVIATION_CLASSIFICATION,
          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.DEVIATION_CLASSIFICATION,
            runs: runsForDeviation,
          }),
          isStatusOngoing: runsForDeviation
            ? isStatusOngoing(runsForDeviation)
            : false,
        },
      ],
      [
        {
          ...algorithmRunBaseExtended,
          key: '2',
          smartFunctionName: t(
            'workspaceDetailsPage.riskReliefSmartFunction.rootCauseDiscovery'
          ),
          smartFunctionType: riskReliefSmartFunctionTypes.ROOT_CAUSE_DISCOVERY,
          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.ROOT_CAUSE_DISCOVERY,
            runs: runsForDeviation,
          }),
          isStatusOngoing: runsForDeviation
            ? isStatusOngoing(runsForDeviation)
            : false,
        },
      ],
      [
        {
          ...algorithmRunBaseExtended,
          key: '3',
          smartFunctionName: t(
            'workspaceDetailsPage.riskReliefSmartFunction.capaGeneration'
          ),
          smartFunctionType: riskReliefSmartFunctionTypes.CAPA_GENERATION,
          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.CAPA_GENERATION,
            runs: runsForDeviation,
          }),
          isStatusOngoing: runsForDeviation
            ? isStatusOngoing(runsForDeviation)
            : false,
        },
      ],
    ]

    return riskReliefSmartFunctions
  }, [
    deviationId,
    selectedWorkspace,
    algorithmRunBase,
    isStatusOngoing,
    calculateStatus,
  ])

  const calculateStatusLabQr = useCallback(
    ({ status, runs }: CalculateStatusProps) => {
      const latestRun = runs[runs.length - 1]
      if (latestRun?.nodeStatuses?.OFR_FILING === algorithmRunStatuses.FAILED) {
        return algorithmRunStatuses.FAILED
      }

      return calculateStatus({ status, runs })
    },
    [calculateStatus]
  )

  const getCalculatedRecord = useCallback(
    (record: ExtendedAlgorithmRun) => {
      const isRiskRelief =
        selectedWorkspace?.typeCode === workspaceTypes.RISK_REL
      if (!isRiskRelief) return record

      const runs = selectedWorkspace.algorithmRuns?.filter((run) => {
        const config = run?.config as AlgorithmRunRiskReliefConfig
        return config?.deviations && config.deviationId === deviationId
      })

      const latestRun = runs?.[runs.length - 1]
      if (!latestRun) return record

      const shouldInjectSmartFunctionType = [
        riskReliefSmartFunctionTypes.CAPA_GENERATION,
        riskReliefSmartFunctionTypes.ROOT_CAUSE_DISCOVERY,
        riskReliefSmartFunctionTypes.DEVIATION_CLASSIFICATION,
      ].includes(record.smartFunctionType as RiskReliefSmartFunctionType)

      return shouldInjectSmartFunctionType
        ? ({
            ...latestRun,
            smartFunctionType: record.smartFunctionType,
          } as RiskReliefAlgorithmRun)
        : latestRun
    },
    [selectedWorkspace, deviationId]
  )

  const getLatestMatchingRunIdBasedOnConfig = useCallback(
    <T>({ runs, config, matchFn }: GetLatestMatchingRunIdProps<T>): string => {
      const matchingRuns = runs.filter((run) => {
        const configuration = config(run.config as AlgorithmRunConfig) // cast to generic config type and match
        return matchFn(configuration)
      })

      return matchingRuns.length > 0
        ? matchingRuns[matchingRuns.length - 1]?.id ?? '0'
        : '0'
    },
    []
  )

  const getCalculatedRecordId = useCallback(
    (recordId: string) => {
      if (!selectedWorkspace) return recordId

      const { typeCode, algorithmRuns = [] } = selectedWorkspace

      switch (typeCode) {
        case workspaceTypes.RISK_REL:
          return getLatestMatchingRunIdBasedOnConfig<AlgorithmRunRiskReliefConfig>(
            {
              runs: algorithmRuns,
              config: (config) => config as AlgorithmRunRiskReliefConfig,
              matchFn: (config) =>
                !!config?.deviations && config.deviationId === deviationId,
            }
          )

        case workspaceTypes.OFR_QR:
          return getLatestMatchingRunIdBasedOnConfig<AlgorithmRunConformityCheckConfig>(
            {
              runs: algorithmRuns,
              config: (config) => config as AlgorithmRunConformityCheckConfig,
              matchFn: (config) =>
                // documentObjectId in alg run config is the same as the documentId in the url
                !!config?.documentObjectId &&
                config.documentObjectId === documentId,
            }
          )

        default:
          return recordId
      }
    },
    [
      selectedWorkspace,
      deviationId,
      documentId,
      getLatestMatchingRunIdBasedOnConfig,
    ]
  )

  const conformityCheckSmartFunctions: ExtendedAlgorithmRun[][] =
    useMemo(() => {
      if (!selectedWorkspace?.algorithmRuns) return []

      // Filter runs for the current document
      const runsForDocument = selectedWorkspace.algorithmRuns.filter((run) => {
        const config = run?.config as AlgorithmRunConformityCheckConfig
        return config?.documentObjectId === documentId
      })

      const latestRun = runsForDocument[runsForDocument.length - 1]
      const algorithmRunBaseExtended: ExtendedAlgorithmRun = {
        ...algorithmRunBase,
        actionDate: latestRun?.actionDate,
        algorithmRunIndex: runsForDocument ? runsForDocument.length : 0,
        templateName: !!latestRun?.config
          ? (latestRun?.config as AlgorithmRunConformityCheckConfig)
              .documentTemplateName
          : undefined,
        id: latestRun?.id,
        calculatedRecordId: latestRun?.id ?? '0',
      }

      const iterationFilename =
        (latestRun?.config as AlgorithmRunConformityCheckConfig)
          ?.iterationFilename || NO_DATA_PLACEHOLDER

      const conformityCheckSmartFunctions: ExtendedAlgorithmRun[][] = [
        [
          {
            ...algorithmRunBaseExtended,
            key: '1',
            smartFunctionName: t(
              'workspaceDetailsPage.conformityCheckSmartFunction.qualityReview'
            ),
            smartFunctionType: conformityCheckFunctionTypes.QUALITY_REVIEW,
            iterationFilename,
            calculatedStatus: calculateStatusLabQr({
              status: latestRun?.nodeStatuses?.LABQR,
              runs: runsForDocument,
            }),
            isStatusOngoing:
              runsForDocument.length > 0
                ? isStatusOngoing(runsForDocument)
                : false,
          },
        ],
        [
          {
            ...algorithmRunBaseExtended,
            algorithmRunIndex: 0, // change this when we have protocol execution review
            key: '2',
            disabled: true,
            iterationFilename,
            smartFunctionName: t(
              'workspaceDetailsPage.conformityCheckSmartFunction.protocolExecutionReview'
            ),
            smartFunctionType:
              conformityCheckFunctionTypes.PROTOCOL_EXECUTION_REVIEW,
            calculatedStatus: algorithmRunStatuses.PENDING,
            isStatusOngoing: false,
          },
        ],
      ]

      return conformityCheckSmartFunctions
    }, [
      documentId,
      selectedWorkspace,
      algorithmRunBase,
      isStatusOngoing,
      calculateStatusLabQr,
    ])

  interface GetLatestMatchingRunIdProps<T> {
    runs: AlgorithmRun[]
    config: (config: AlgorithmRunConfig) => T // generic alg config type
    matchFn: (config: T) => boolean
  }

  const flowCytometrySmartFunctions: ExtendedAlgorithmRun[][] = useMemo(() => {
    if (!selectedWorkspace?.algorithmRuns) return []
    const runs = selectedWorkspace?.algorithmRuns

    const latestRun = runs[runs.length - 1]
    const algorithmRunBaseExtended: ExtendedAlgorithmRun = {
      ...algorithmRunBase,
      actionDate: latestRun?.actionDate,
      algorithmRunIndex: runs ? runs.length : 0,
      templateName: !!latestRun?.config
        ? (latestRun?.config as AlgorithmRunConformityCheckConfig)
            .documentTemplateName
        : undefined,
      id: latestRun?.id,
      calculatedRecordId: latestRun?.id ?? '0',
    }

    const flowCytometrySmartFunctionList: ExtendedAlgorithmRun[][] = [
      [
        {
          ...algorithmRunBaseExtended,
          key: '1',
          smartFunctionName: t(
            'workspaceDetailsPage.flowCytometry.compensationMatrixAnalysis'
          ),
          smartFunctionType: conformityCheckFunctionTypes.QUALITY_REVIEW,

          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.ROOT_CAUSE_DISCOVERY,
            runs: runs,
          }),
          isStatusOngoing: runs ? isStatusOngoing(runs) : false,
          isTitleRow: true,
        },

        {
          ...algorithmRunBaseExtended,
          key: '10',
          smartFunctionName: t('workspaceDetailsPage.flowCytometry.matching'),
          smartFunctionType: conformityCheckFunctionTypes.QUALITY_REVIEW,

          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.ROOT_CAUSE_DISCOVERY,
            runs: runs,
          }),
          isStatusOngoing: runs ? isStatusOngoing(runs) : false,
          isChildRow: true,
        },
        {
          ...algorithmRunBaseExtended,
          key: '11',
          smartFunctionName: t('workspaceDetailsPage.flowCytometry.filtering'),
          smartFunctionType: conformityCheckFunctionTypes.QUALITY_REVIEW,

          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.ROOT_CAUSE_DISCOVERY,
            runs: runs,
          }),
          isStatusOngoing: runs ? isStatusOngoing(runs) : false,
          isChildRow: true,
        },
      ],
      [
        {
          ...algorithmRunBaseExtended,
          key: '2',
          smartFunctionName: t(
            'workspaceDetailsPage.flowCytometry.reviewAnalysis'
          ),
          smartFunctionType: conformityCheckFunctionTypes.QUALITY_REVIEW,

          calculatedStatus: calculateStatus({
            status: latestRun?.nodeStatuses?.ROOT_CAUSE_DISCOVERY,
            runs: runs,
          }),
          isStatusOngoing: runs ? isStatusOngoing(runs) : false,
        },
      ],
    ]
    return flowCytometrySmartFunctionList
  }, [algorithmRunBase, calculateStatus, isStatusOngoing, selectedWorkspace])

  return {
    algorithmRunBase,
    riskReliefSmartFunctions,
    isRiskReliefWorkspaceDeviationClosed,
    conformityCheckSmartFunctions,
    flowCytometrySmartFunctions,
    getLatestMatchingRunIdBasedOnConfig,
    isStatusOngoing,
    calculateStatus,
    calculateStatusLabQr,
    getCalculatedRecord,
    getCalculatedRecordId,
  }
}

export default useWorkspaceSmartFunctionData
